// src/pages/SuperuserDashboard.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

// Application configuration
const APPLICATIONS = {
  PART1: {
    id: "PART1",
    name: "MISSING: The Search for Terry Hughes",
    shortName: "MISSING Part I"
  },
  PART2: {
    id: "PART2",
    name: "MISSING: The Final Secret",
    shortName: "MISSING Part II"
  }
};

const SuperuserDashboard = () => {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [newAdminEmail, setNewAdminEmail] = useState("");
  const [selectedSystemSection, setSelectedSystemSection] = useState("PART1");
  const navigate = useNavigate();

  // Check authentication on component mount
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/");
      return;
    }

    // Clear messages after 5 seconds
    const timer = setTimeout(() => {
      setSuccessMessage("");
      setError("");
    }, 5000);
    
    return () => clearTimeout(timer);
  }, [navigate, successMessage, error]);

  // API request helper that uses the token
  const apiRequest = async (url, method = "GET", body = null) => {
    const token = localStorage.getItem("token");
    
    if (!token) {
      navigate("/");
      throw new Error("No authentication token found");
    }
    
    const options = {
      method,
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`
      }
    };
    
    if (body && method !== "GET") {
      options.body = JSON.stringify(body);
    }
    
    const response = await fetch(url, options);
    
    if (response.status === 401) {
      localStorage.removeItem("token");
      navigate("/");
      throw new Error("Session expired. Please log in again.");
    }
    
    if (!response.ok) {
      throw new Error(`Request failed (${response.status})`);
    }
    
    return await response.json();
  };

  // Fetch all admins
  const fetchAdmins = async () => {
    setLoading(true);
    setError("");
    
    try {
      const data = await apiRequest("/superuser/list-admins");
      setAdmins(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error fetching admins:", err);
      setError(`Failed to fetch data: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Add new admin - now creates admin with access to all applications
  const addAdmin = async () => {
    if (!newAdminEmail) {
      setError("Email is required");
      return;
    }
    
    setLoading(true);
    setError("");
    setSuccessMessage("");
    
    try {
      // Generate invitation token
      const inviteToken = Math.random().toString(36).substring(2, 15) + 
                         Math.random().toString(36).substring(2, 15);
      
      // Store the token in DynamoDB for verification - no application specified
      await apiRequest("/superuser/store-invitation", "POST", {
        email: newAdminEmail,
        token: inviteToken,
        userType: "Admin",
        expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString() // Expires in 7 days
      });
      
      // Send invitation email - no specific application in the subject
      await apiRequest("/superuser/send-invitation", "POST", {
        email: newAdminEmail,
        inviteToken: inviteToken,
        emailSubject: `Welcome to MISSING - Admin Access Invitation`,
      });
      
      setSuccessMessage(`Admin invitation sent to ${newAdminEmail} with access to all MISSING applications!`);
      setNewAdminEmail("");
      fetchAdmins();
    } catch (err) {
      console.error("Error adding admin:", err);
      setError(`Failed to send admin invitation: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Remove admin
  const removeAdmin = async (adminEmail) => {
    if (!window.confirm(`Are you sure you want to remove ${adminEmail} as an admin?`)) {
      return;
    }
    
    setLoading(true);
    setError("");
    setSuccessMessage("");
    
    try {
      await apiRequest("/superuser/remove-admin", "POST", {
        email: adminEmail
      });
      
      setSuccessMessage(`Admin ${adminEmail} removed successfully!`);
      fetchAdmins();
    } catch (err) {
      console.error("Error removing admin:", err);
      setError(`Failed to remove admin: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Handle logout
  const handleLogout = () => {
    localStorage.removeItem("token");
    navigate("/");
  };

  return (
    <div style={{ padding: "1rem", maxWidth: 700, margin: "0 auto" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
        <h1 style={{ fontSize: "1.5rem", margin: 0 }}>Superuser Dashboard</h1>
        <button 
          onClick={handleLogout}
          style={{ 
            padding: "0.5rem 1rem", 
            backgroundColor: "#f44336", 
            color: "white", 
            border: "none",
            borderRadius: "4px",
            cursor: "pointer" 
          }}
        >
          Logout
        </button>
      </div>
      
      {/* Success Message */}
      {successMessage && (
        <div style={{ 
          color: "green", 
          marginBottom: "1rem", 
          padding: "0.5rem", 
          backgroundColor: "#e8f5e9",
          borderRadius: "4px" 
        }}>
          {successMessage}
        </div>
      )}
      
      {/* Error Message */}
      {error && (
        <div style={{ 
          color: "red", 
          marginBottom: "1rem", 
          padding: "0.5rem", 
          backgroundColor: "#ffebee",
          borderRadius: "4px" 
        }}>
          {error}
        </div>
      )}
      
      {/* Add Admin Section */}
      <div style={{ 
        border: "1px solid #ccc", 
        padding: "1rem", 
        marginBottom: "1rem",
        borderRadius: "4px" 
      }}>
        <h2 style={{ fontSize: "1.2rem", marginTop: 0 }}>Add MISSING Admin</h2>
        <div style={{ display: "flex", gap: "1rem", marginTop: "0.5rem", flexWrap: "wrap" }}>
          <input
            placeholder="Admin Email"
            type="email"
            value={newAdminEmail}
            onChange={(e) => setNewAdminEmail(e.target.value)}
            style={{ 
              flex: 1, 
              padding: "0.5rem",
              borderRadius: "4px",
              border: "1px solid #ccc",
              minWidth: "200px"
            }}
          />
          <button 
            onClick={addAdmin} 
            disabled={loading || !newAdminEmail}
            style={{ 
              padding: "0.5rem 1rem",
              backgroundColor: "#673ab7",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: (loading || !newAdminEmail) ? "default" : "pointer"
            }}
          >
            {loading ? "Adding..." : "Send Invitation"}
          </button>
        </div>
        <p style={{ fontSize: "0.9rem", color: "#666", marginTop: "0.5rem" }}>
          An invitation email will be sent to the admin with registration instructions.
          Admins will have access to all MISSING applications.
        </p>
      </div>
      
      {/* List Admins Section */}
      <div style={{ 
        border: "1px solid #ccc", 
        padding: "1rem",
        borderRadius: "4px",
        marginBottom: "1rem"
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h2 style={{ fontSize: "1.2rem", marginTop: 0 }}>
            MISSING Admins
          </h2>
          <button 
            onClick={fetchAdmins} 
            disabled={loading}
            style={{ 
              padding: "0.5rem 1rem",
              backgroundColor: "#2196F3",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: loading ? "default" : "pointer"
            }}
          >
            {loading ? "Loading..." : "Refresh"}
          </button>
        </div>
        
        {loading ? (
          <p>Loading admins...</p>
        ) : admins.length === 0 ? (
          <p>No admins found. Click "Refresh" to fetch the latest data.</p>
        ) : (
          <ul style={{ listStyleType: "none", padding: 0 }}>
            {admins.map((admin) => (
              <li 
                key={admin.email} 
                style={{ 
                  padding: "0.75rem",
                  marginBottom: "0.5rem",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  backgroundColor: "#f5f5f5",
                  borderRadius: "4px"
                }}
              >
                <div>
                  <div style={{ fontWeight: "bold" }}>{admin.email}</div>
                  <div style={{ fontSize: "0.8rem", color: "#666" }}>
                    Added: {new Date(admin.createdAt).toLocaleDateString()}
                  </div>
                </div>
                
                <button
                  onClick={() => removeAdmin(admin.email)}
                  style={{ 
                    padding: "0.25rem 0.5rem",
                    backgroundColor: "#f44336",
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                    fontSize: "0.8rem",
                    cursor: "pointer"
                  }}
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      
      {/* Application Selector for System Configuration */}
      <div style={{ 
        border: "1px solid #ccc", 
        padding: "1rem", 
        marginBottom: "1rem",
        borderRadius: "4px" 
      }}>
        <h2 style={{ fontSize: "1.2rem", marginTop: 0 }}>System Configuration</h2>
        <div style={{ marginBottom: "1rem" }}>
          <p style={{ fontSize: "0.9rem", color: "#666" }}>
            Select an application to view or edit its system configuration:
          </p>
          <div style={{ display: "flex", gap: "1rem", marginTop: "0.5rem" }}>
            {Object.keys(APPLICATIONS).map(appId => (
              <button
                key={appId}
                onClick={() => setSelectedSystemSection(appId)}
                style={{
                  padding: "0.5rem 1rem",
                  backgroundColor: selectedSystemSection === appId ? "#4CAF50" : "#e0e0e0",
                  color: selectedSystemSection === appId ? "white" : "black",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontWeight: selectedSystemSection === appId ? "bold" : "normal"
                }}
              >
                {APPLICATIONS[appId].shortName}
              </button>
            ))}
          </div>
        </div>
        
        <div style={{ padding: "1rem", backgroundColor: "#f5f5f5", borderRadius: "4px" }}>
          <h3 style={{ fontSize: "1.1rem", marginTop: 0 }}>
            {APPLICATIONS[selectedSystemSection].name} Configuration
          </h3>
          <p style={{ color: "#666" }}>
            This section will allow you to configure settings for this specific application:
          </p>
          <ul style={{ color: "#666" }}>
            <li>AWS AppStream settings for {APPLICATIONS[selectedSystemSection].shortName}</li>
            <li>License allocation limits</li>
            <li>Email templates</li>
            <li>Certificate design</li>
          </ul>
          <p style={{ color: "#666", fontStyle: "italic" }}>
            Functionality coming soon. For now, please contact system support for configuration changes.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SuperuserDashboard;